import React from 'react';
import {
  Avatar,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import moment from 'moment';

import { IQuiz } from '../../../../store/QuizSlice';
import { getSubjectColorNew, getSubjectIconName } from '../../../../helper';

interface QuizListItemProps {
  quiz: IQuiz;
  onClick?: () => void;
  matchBotoomStyles?: boolean;
  onTakeQuiz: () => void;
}

const CustomAvatar = ({
  subject,
  size,
  margin,
}: {
  subject: string;
  size: number;
  margin?: string;
}) => {
  return (
    <Avatar
      src={`${
        process.env.PUBLIC_URL
      }/images/landingPage/icon-${getSubjectIconName(subject)}.png`}
      alt={subject}
      style={{
        margin,
        backgroundColor: `${getSubjectColorNew(subject)}1A`,
        width: `${size}px`,
        height: `${size}px`,
      }}
    />
  );
};

const QuizListItem: React.FC<QuizListItemProps> = ({
  quiz,
  onClick = undefined,
  matchBotoomStyles = false,
  onTakeQuiz,
}) => {
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  const quizTopicData = quiz.topic;
  const quizSubTopics = quiz.subTopics;
  const lastUpdatedText = `Last updated at: ${moment(quiz.updatedAt).format(
    'YYYY-MM-DD hh:mm A'
  )}`;

  const attemptsText = `${(quiz.submissionCount || 0)
    .toString()
    .padStart(2, '0')} Attempt${
    quiz.submissionCount && quiz.submissionCount > 1 ? 's' : ''
  }`;

  let subTopicText = '';
  let takeQuizButtonText = 'Retake Quiz';

  if (!quiz.submissionCount) {
    takeQuizButtonText = 'Take a Quiz';
  }

  quizSubTopics.forEach((quizSubTopic, index) => {
    const subTopicName = quizSubTopic.subTopic?.name || '';

    if (!subTopicText.includes(subTopicName)) {
      if (index > 0) {
        subTopicText += ', ';
      }

      subTopicText += subTopicName;
    }
  });

  const containerStyles: React.CSSProperties = {
    borderLeft: '1px solid #E2E8F0',
    borderTop: '1px solid #E2E8F0',
    borderRight: '1px solid #E2E8F0',
    borderBottom: '1px solid #E2E8F0',
    borderTopLeftRadius: '5px',
    borderTopRightRadius: '5px',
    borderBottomLeftRadius: '5px',
    borderBottomRightRadius: '5px',
  };

  if (matchBotoomStyles) {
    containerStyles.borderBottomLeftRadius = 0;
    containerStyles.borderBottomRightRadius = 0;
    containerStyles.borderBottom = '1px solid transparent';
  }

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleRetakeQuiz = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();

    onTakeQuiz();
  };

  if (isXsScreen) {
    return (
      <Card elevation={0} style={containerStyles}>
        <CardActionArea
          disableRipple={!onclick}
          disableTouchRipple={!onclick}
          onClick={handleCardClick}
        >
          <CardContent style={{ padding: '16px' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    className="ellipsis-text"
                  >
                    <CustomAvatar
                      subject={quizTopicData.subject}
                      size={40}
                      margin="0 10px 0 0"
                    />
                    <Typography
                      color={getSubjectColorNew(quizTopicData.subject)}
                      variant="subtitle2"
                      fontWeight={600}
                      className="ellipsis-text"
                      title={quizTopicData.subject}
                      style={{ marginRight: '10px' }}
                    >
                      {quizTopicData.subject}
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={handleRetakeQuiz}
                    style={{
                      width: '125px',
                      fontSize: '12px',
                      borderColor: '#01A79F',
                      color: '#01A79F',
                      backgroundColor: '#F8FAFC',
                    }}
                  >
                    {takeQuizButtonText}
                  </Button>
                </Stack>
                <div style={{ margin: '8px 0' }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {subTopicText}
                  </Typography>
                </div>
                {quiz.updatedAt ? (
                  <div style={{ marginBottom: '8px' }}>
                    <Typography variant="subtitle2" fontWeight={500}>
                      Last updated at: {lastUpdatedText}
                    </Typography>
                  </div>
                ) : null}
                <Typography variant="subtitle2" fontWeight={500}>
                  <span>{quizTopicData.curriculum}</span>
                  &nbsp; | &nbsp;
                  <span>{quizTopicData.name}</span>
                  &nbsp; | &nbsp;
                  <span style={{ textDecoration: 'underline' }}>
                    {attemptsText}
                  </span>
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  }

  return (
    <Card elevation={0} style={containerStyles}>
      <CardActionArea
        disableRipple={!onclick}
        disableTouchRipple={!onclick}
        onClick={handleCardClick}
      >
        <CardContent style={{ padding: '16px' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item style={{ width: '70px' }}>
              <CustomAvatar subject={quizTopicData.subject} size={50} />
            </Grid>
            <Grid item style={{ width: 'calc(100% - 210px)' }}>
              <Stack>
                <div>
                  <Typography
                    fontWeight={600}
                    className="ellipsis-text"
                    title={subTopicText}
                  >
                    {subTopicText}
                  </Typography>
                </div>
              </Stack>
              <Typography variant="subtitle2" fontWeight={500}>
                <span
                  style={{
                    color: getSubjectColorNew(quizTopicData.subject),
                  }}
                >
                  {quizTopicData.subject}
                </span>
                &nbsp; | &nbsp;
                <span>{quizTopicData.curriculum}</span>
                &nbsp; | &nbsp;
                <span>{quizTopicData.name}</span>
                {quiz.updatedAt ? (
                  <>
                    &nbsp; | &nbsp;
                    <span>{lastUpdatedText}</span>
                  </>
                ) : null}
                &nbsp; | &nbsp;
                <span style={{ textDecoration: 'underline' }}>
                  {attemptsText}
                </span>
              </Typography>
            </Grid>
            <Grid item style={{ width: '120px' }}>
              <Button
                size="small"
                color="primary"
                variant="outlined"
                onClick={handleRetakeQuiz}
                style={{
                  borderColor: '#01A79F',
                  color: '#01A79F',
                  backgroundColor: '#F8FAFC',
                }}
              >
                {takeQuizButtonText}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default QuizListItem;
